import { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import {
  Target as TargetIcon, CheckCircle2, Circle, Loader2, RefreshCw, Trophy, Zap, Calendar,
  Dumbbell, ChevronDown, ChevronUp,
} from "lucide-react";
import { WorkoutLogModal } from "@/components/app/WorkoutLogModal";

const KIND_ICON = { daily: Zap, weekly: Calendar, "one-time": TargetIcon };
const KIND_COLOR = {
  daily: "bg-[#FFEDD5] text-[#C2410C]",
  weekly: "bg-[#FEF3C7] text-[#B45309]",
  "one-time": "bg-[#DBEAFE] text-[#1E40AF]",
};

export function CoachPlanPanel({ coach, coachLabel, refreshKey, autoGenerate }) {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [adjusting, setAdjusting] = useState(false);
  const [adjustNote, setAdjustNote] = useState("");
  const [showAdjust, setShowAdjust] = useState(false);
  const [expandedTaskId, setExpandedTaskId] = useState(null);
  const [logTask, setLogTask] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/coach/plans", { params: { coach } });
      setPlan(data.plan);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [coach]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const generate = useCallback(async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/coach/plans/generate?coach=${coach}`);
      setPlan(data.plan);
      toast.success("Your plan is ready.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setGenerating(false); }
  }, [coach]);

  // Auto-generate when the coach signals BUILD_PLAN and no active plan exists.
  useEffect(() => {
    if (autoGenerate && !generating && !loading && !plan) {
      generate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoGenerate]);

  const toggle = async (taskId, completed) => {
    // Optimistic update.
    setPlan((p) => ({
      ...p,
      tasks: p.tasks.map((t) => t.id === taskId
        ? { ...t, completed_at: completed ? new Date().toISOString() : null }
        : t),
    }));
    try {
      await api.post(`/coach/plans/${plan.id}/tasks/${taskId}/toggle`, { completed });
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      load();
    }
  };

  const adjust = async () => {
    setAdjusting(true);
    try {
      const { data } = await api.post(`/coach/plans/${plan.id}/adjust`, { note: adjustNote });
      setPlan(data.plan);
      setAdjustNote("");
      setShowAdjust(false);
      toast.success("Plan adjusted.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setAdjusting(false); }
  };

  if (loading) {
    return <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-[#FF7E67]" /></div>;
  }

  if (!plan) {
    return (
      <div className="rounded-3xl border-2 border-dashed border-[#FFE8E3] bg-white p-6 text-center" data-testid="no-plan-yet">
        <TargetIcon className="mx-auto h-6 w-6 text-[#FF7E67]" strokeWidth={2.5} />
        <p className="mt-2 font-nunito text-sm font-bold text-[#2D3748]">No plan yet</p>
        <p className="mt-1 font-quicksand text-xs text-[#718096]">Chat with your {coachLabel} — once you&rsquo;ve shared enough context they&rsquo;ll offer to build a plan you can check off.</p>
        <button onClick={generate} disabled={generating} data-testid="generate-plan-btn"
          className="mt-4 inline-flex items-center gap-2 rounded-full bg-sunrise px-4 py-2 font-nunito text-sm font-bold text-white shadow-glow-coral hover:scale-[1.02] disabled:opacity-60">
          {generating
            ? <><Loader2 className="h-4 w-4 animate-spin" /> Building your plan…</>
            : <>Build plan now</>}
        </button>
      </div>
    );
  }

  const total = plan.tasks?.length || 0;
  const done = plan.tasks?.filter((t) => t.completed_at).length || 0;
  const progress = total ? Math.round(done / total * 100) : 0;

  return (
    <div className="rounded-3xl border border-[#FFE8E3] bg-white p-5" data-testid="coach-plan-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[#C2410C]">Your active plan</p>
          <h3 className="mt-1 font-nunito text-lg font-bold text-[#2D3748]" data-testid="plan-title">{plan.title}</h3>
          {plan.goal_summary && <p className="mt-0.5 font-quicksand text-xs text-[#718096]">{plan.goal_summary}</p>}
        </div>
        <div className="flex items-center gap-2">
          {progress >= 100
            ? <span className="flex items-center gap-1 rounded-full bg-[#E6FBF5] px-3 py-1 font-nunito text-xs font-bold text-[#06D6A0]"><Trophy className="h-3 w-3" /> Complete</span>
            : <span className="rounded-full bg-[#FFE8E3] px-3 py-1 font-nunito text-xs font-bold text-[#C2410C]">{done}/{total} · {progress}%</span>}
          <button onClick={generate} disabled={generating}
            data-testid="regenerate-plan-btn" title="Regenerate from scratch"
            className="rounded-full p-2 text-[#718096] hover:bg-[#FFF9F5] hover:text-[#FF7E67] disabled:opacity-60">
            <RefreshCw className={`h-4 w-4 ${generating ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-[#FFE8E3]">
        <motion.div layout className="h-full rounded-full bg-sunrise" style={{ width: `${progress}%` }} />
      </div>

      {plan.milestones?.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2" data-testid="plan-milestones">
          {plan.milestones.map((m, i) => (
            <span key={i} className="rounded-full bg-[#FFF4E6] px-2.5 py-1 font-quicksand text-[11px] font-semibold text-[#B45309]">
              Wk {m.target_week}: {m.title}
            </span>
          ))}
        </div>
      )}

      <div className="mt-5 space-y-2" data-testid="plan-tasks">
        <AnimatePresence>
          {plan.tasks?.map((t) => {
            const Icon = KIND_ICON[t.kind] || TargetIcon;
            const isDone = !!t.completed_at;
            const hasExercises = Array.isArray(t.exercises) && t.exercises.length > 0;
            const isExpanded = expandedTaskId === t.id;
            return (
              <motion.div key={t.id} layout initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                className={`rounded-2xl border transition-colors ${
                  isDone ? "border-[#06D6A0]/30 bg-[#E6FBF5]" : "border-[#FFE8E3] bg-[#FFF9F5]"}`}
                data-testid={`plan-task-${t.id}`}>
                <div className="flex items-start gap-3 p-3">
                  <button onClick={() => toggle(t.id, !isDone)}
                    data-testid={`toggle-task-${t.id}`}
                    className="mt-0.5 shrink-0" title={isDone ? "Mark undone" : "Mark done"}>
                    {isDone
                      ? <CheckCircle2 className="h-5 w-5 text-[#06D6A0]" strokeWidth={2.5} />
                      : <Circle className="h-5 w-5 text-[#A0AEC0] hover:text-[#FF7E67]" strokeWidth={2.5} />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className={`font-nunito text-sm font-bold ${isDone ? "text-[#06D6A0] line-through" : "text-[#2D3748]"}`}>{t.title}</p>
                    {t.notes && <p className="mt-0.5 font-quicksand text-xs text-[#718096]">{t.notes}</p>}
                    <div className="mt-1 flex flex-wrap items-center gap-1.5">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-nunito text-[10px] font-bold uppercase tracking-wider ${KIND_COLOR[t.kind] || KIND_COLOR.weekly}`}>
                        <Icon className="h-3 w-3" strokeWidth={2.5} /> {t.kind}
                      </span>
                      {t.when && <span className="font-quicksand text-[11px] text-[#718096]">{t.when}</span>}
                      {hasExercises && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-[#FFF4E6] px-2 py-0.5 font-nunito text-[10px] font-bold text-[#B45309]">
                          <Dumbbell className="h-3 w-3" strokeWidth={2.5} /> {t.exercises.length} moves
                        </span>
                      )}
                    </div>
                  </div>
                  {hasExercises && (
                    <div className="flex flex-col items-end gap-1">
                      <button
                        onClick={() => setLogTask(t)}
                        data-testid={`log-workout-${t.id}`}
                        className="rounded-full bg-sunrise px-3 py-1 font-nunito text-[11px] font-bold text-white shadow-glow-coral hover:scale-[1.03]"
                      >Log workout</button>
                      <button
                        onClick={() => setExpandedTaskId(isExpanded ? null : t.id)}
                        data-testid={`expand-task-${t.id}`}
                        className="inline-flex items-center gap-1 text-[11px] font-bold text-[#C2410C] hover:underline"
                      >
                        {isExpanded ? <>Hide <ChevronUp className="h-3 w-3" /></> : <>View <ChevronDown className="h-3 w-3" /></>}
                      </button>
                    </div>
                  )}
                </div>
                {hasExercises && isExpanded && (
                  <div className="border-t border-[#FFE8E3] px-3 pb-3 pt-3">
                    <div className="grid gap-3 sm:grid-cols-2">
                      {t.exercises.map((e, i) => (
                        <div key={i} className="rounded-2xl border border-[#FFE8E3] bg-white p-3"
                             data-testid={`exercise-${t.id}-${i}`}>
                          {e.image_url && (
                            <img src={e.image_url} alt={e.name}
                                 loading="lazy"
                                 className="mb-2 aspect-video w-full rounded-xl border border-[#FFE8E3] bg-[#FFF9F5] object-contain" />
                          )}
                          <p className="font-nunito text-sm font-bold text-[#2D3748]">{e.name}</p>
                          <p className="mt-0.5 font-nunito text-xs font-bold text-[#C2410C]">
                            {e.sets} × {e.reps}
                          </p>
                          {e.weight_hint && (
                            <p className="mt-1 font-quicksand text-[11px] text-[#718096]">
                              <span className="font-bold text-[#B45309]">Weight:</span> {e.weight_hint}
                            </p>
                          )}
                          {e.form_cue && (
                            <p className="mt-1 font-quicksand text-[11px] italic text-[#718096]">
                              &ldquo;{e.form_cue}&rdquo;
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-[#FFE8E3] pt-4">
        {!showAdjust ? (
          <button onClick={() => setShowAdjust(true)} data-testid="adjust-plan-btn"
            className="inline-flex items-center gap-2 rounded-full border border-[#FFE8E3] px-4 py-2 font-nunito text-sm font-bold text-[#C2410C] hover:bg-[#FFF9F5]">
            <RefreshCw className="h-4 w-4" strokeWidth={2.5} /> Adjust plan
          </button>
        ) : (
          <div className="flex flex-1 flex-wrap items-center gap-2">
            <input value={adjustNote} onChange={(e) => setAdjustNote(e.target.value)}
              placeholder="What changed? e.g. only 3 days/week available now"
              data-testid="adjust-note-input"
              className="h-11 min-w-[220px] flex-1 rounded-full border border-[#FFE8E3] bg-[#FFF9F5] px-4 font-quicksand text-sm outline-none focus:border-[#FF7E67]" />
            <button onClick={adjust} disabled={adjusting || !adjustNote.trim()} data-testid="adjust-confirm-btn"
              className="inline-flex items-center gap-2 rounded-full bg-sunrise px-4 py-2 font-nunito text-sm font-bold text-white shadow-glow-coral disabled:opacity-60">
              {adjusting ? <><Loader2 className="h-4 w-4 animate-spin" /> Adjusting…</> : "Rebuild"}
            </button>
            <button onClick={() => { setShowAdjust(false); setAdjustNote(""); }}
              className="rounded-full border border-[#FFE8E3] px-3 py-2 font-nunito text-sm font-bold text-[#718096] hover:bg-[#FFF9F5]">
              Cancel
            </button>
          </div>
        )}
      </div>

      {logTask && (
        <WorkoutLogModal
          planId={plan.id}
          task={logTask}
          onClose={() => setLogTask(null)}
          onSaved={() => load()}
        />
      )}
    </div>
  );
}
